import {
  applyNodeChanges,
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type NodeChange,
} from '@xyflow/react'
import {Maximize2} from 'lucide-react'
import '@xyflow/react/dist/style.css'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {ApiNode, type ApiNodeData} from './ApiNode'
import {CategoryGroupNode, type CategoryGroupNodeData} from './CategoryGroupNode'
import {FilteredHint} from './FilteredHint'
import {layoutWithGroups} from '@/lib/layout'
import {useFilteredEntries, useStore} from '@/store'
import type {CategoryId} from '@/lib/types'

const nodeTypes = {api: ApiNode, 'category-group': CategoryGroupNode}

function GraphInner() {
  const entries = useFilteredEntries()
  const allEntries = useStore((s) => s.entries)
  const relationships = useStore((s) => s.relationships)
  const runtime = useStore((s) => s.runtime)
  const select = useStore((s) => s.select)
  const sortMode = useStore((s) => s.sortMode)

  const [layoutNodes, setLayoutNodes] = useState<Node[]>([])
  const [layoutEdges, setLayoutEdges] = useState<Edge[]>([])
  const [layingOut, setLayingOut] = useState(true)
  const rf = useReactFlow()

  // Edges: only those whose endpoints are in the filtered view
  const visibleEdges = useMemo(() => {
    const visibleIds = new Set(entries.map((e) => e.id))
    return relationships.filter((r) => visibleIds.has(r.from) && visibleIds.has(r.to))
  }, [entries, relationships])

  // Run ELK layout when the visible-node set changes
  useEffect(() => {
    let cancelled = false
    if (entries.length === 0) {
      setLayoutNodes([])
      setLayoutEdges([])
      setLayingOut(false)
      return
    }
    setLayingOut(true)

    layoutWithGroups(
      entries.map((e) => ({
        id: e.id,
        category: e.category,
        title: e.title,
        baseline: e.baseline,
        baselineYear: e.baselineYear,
      })),
      visibleEdges.map((r, i) => ({id: `e${i}`, source: r.from, target: r.to})),
      sortMode
    )
      .then(({nodes: positions, groups}) => {
        if (cancelled) return

        // Which sides actually have an edge attached? Used to suppress the
        // little handle dots on otherwise-disconnected nodes.
        const hasIncoming = new Set<string>()
        const hasOutgoing = new Set<string>()
        for (const e of visibleEdges) {
          hasOutgoing.add(e.from)
          hasIncoming.add(e.to)
        }

        // Subflows: only in category mode. Other modes are flat — grouping
        // by Baseline / letter / connected-component into visual containers
        // would be more visual noise than insight there.
        const useSubflows = sortMode === 'category'

        // Build a lookup: category-id → group rect. Used to (a) emit the
        // parent nodes and (b) compute child positions relative to the
        // parent (ReactFlow expects child positions to be parent-relative).
        const groupByCat = new Map<CategoryId, (typeof groups)[number]>()
        if (useSubflows) {
          for (const g of groups) {
            if (g.id.startsWith('cat:')) {
              groupByCat.set(g.id.slice(4) as CategoryId, g)
            }
          }
        }

        // Count entries per category for the label tab badge
        const countByCat = new Map<CategoryId, number>()
        if (useSubflows) {
          for (const e of entries) {
            countByCat.set(e.category, (countByCat.get(e.category) ?? 0) + 1)
          }
        }

        // Parent nodes FIRST in the array — ReactFlow needs to render
        // containers before children to size and clip them correctly.
        const parentNodes: Node[] = useSubflows
          ? Array.from(groupByCat.entries()).map(([catId, g]) => ({
              id: g.id,
              type: 'category-group',
              position: {x: g.x, y: g.y},
              style: {width: g.width, height: g.height},
              data: {
                categoryId: catId,
                count: countByCat.get(catId) ?? 0,
              } satisfies CategoryGroupNodeData,
              draggable: true,
              selectable: false,
              // Parent must render below children
              zIndex: 0,
            }))
          : []

        const childNodes: Node[] = entries.map((entry) => {
          const abs = positions[entry.id] ?? {x: 0, y: 0}
          const parent = useSubflows ? groupByCat.get(entry.category) : undefined
          // ReactFlow: when parentId is set, position is relative to parent.
          const position = parent
            ? {x: abs.x - parent.x, y: abs.y - parent.y}
            : abs
          return {
            id: entry.id,
            type: 'api',
            position,
            parentId: parent?.id,
            extent: parent ? ('parent' as const) : undefined,
            data: {
              entry,
              runtime: runtime[entry.id],
              hasIncoming: hasIncoming.has(entry.id),
              hasOutgoing: hasOutgoing.has(entry.id),
            } satisfies ApiNodeData,
            draggable: true,
            selectable: true,
            zIndex: 1,
          }
        })

        const flowNodes: Node[] = [...parentNodes, ...childNodes]

        const flowEdges: Edge[] = visibleEdges.map((r, i) => ({
          id: `e${i}-${r.from}-${r.to}`,
          source: r.from,
          target: r.to,
          label: r.label,
          type: 'simplebezier',
          labelStyle: {fontSize: 9, fill: 'var(--color-muted)'},
          labelBgStyle: {fill: 'var(--color-bg)'},
          labelBgPadding: [4, 2],
        }))

        setLayoutNodes(flowNodes)
        setLayoutEdges(flowEdges)
        setLayingOut(false)

        // Fit after a frame so nodes are measured. Cap maxZoom so the
        // initial view stays readable even with many nodes — users can
        // zoom out via the controls/minimap to see everything.
        requestAnimationFrame(() =>
          rf.fitView({padding: 0.15, duration: 400, maxZoom: 0.9, minZoom: 0.3})
        )
      })
      .catch((err) => {
        console.error('Layout failed', err)
        setLayingOut(false)
      })

    return () => {
      cancelled = true
    }
  }, [entries, visibleEdges, runtime, rf, sortMode])

  // Update runtime on existing nodes without re-laying-out. Preserve the
  // connectivity flags computed during layout — they only depend on edges,
  // not runtime detection.
  useEffect(() => {
    setLayoutNodes((prev) =>
      prev.map((n) => {
        const entry = allEntries.find((e) => e.id === n.id)
        if (!entry) return n
        const prevData = n.data as unknown as ApiNodeData
        return {
          ...n,
          data: {
            entry,
            runtime: runtime[n.id],
            hasIncoming: prevData.hasIncoming,
            hasOutgoing: prevData.hasOutgoing,
          } satisfies ApiNodeData,
        }
      })
    )
  }, [runtime, allEntries])

  // React Flow is in controlled mode (we pass `nodes` instead of `defaultNodes`),
  // so it cannot mutate positions on drag without our help. applyNodeChanges
  // is the official helper that turns position/select/etc. changes into
  // the new array shape we need to store.
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setLayoutNodes((nds) => applyNodeChanges(changes, nds))
  }, [])

  return (
    <div className="relative h-full w-full">
    <ReactFlow
      nodes={layoutNodes}
      edges={layoutEdges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onNodeClick={(_, node) => select(node.id)}
      onPaneClick={() => select(null)}
      nodesConnectable={false}
      nodesDraggable
      panOnScroll
      zoomOnDoubleClick={false}
      minZoom={0.15}
      maxZoom={2.5}
      proOptions={{hideAttribution: false}}
      fitView
      fitViewOptions={{padding: 0.15, maxZoom: 0.9, minZoom: 0.3}}
    >
      <Background variant={BackgroundVariant.Dots} gap={28} size={1} color="var(--color-border)" />
      <Controls position="bottom-right" showInteractive={false}>
        <ControlButton
          onClick={() => rf.fitView({padding: 0.05, duration: 400, maxZoom: 1.5})}
          title="Fit everything in view"
        >
          <Maximize2 size={12} />
        </ControlButton>
      </Controls>

      {layingOut && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[var(--color-muted)]">
          Laying out…
        </div>
      )}
    </ReactFlow>

    {/* Empty state lives outside ReactFlow so its buttons are clickable
         — ReactFlow's pane otherwise captures pointer events on children. */}
    {!layingOut && entries.length === 0 && (
      <div className="pointer-events-auto absolute inset-0 z-10 overflow-y-auto bg-[var(--color-bg)]/60 backdrop-blur-sm">
        <EmptyState />
      </div>
    )}
    </div>
  )
}

function EmptyState() {
  const resetFilters = useStore((s) => s.resetFilters)
  const search = useStore((s) => s.search)
  const hasSearch = search.trim().length > 1

  return (
    <div className="pointer-events-auto absolute inset-0 flex flex-col items-center justify-center gap-4 overflow-y-auto px-4 py-6">
      <div className="max-w-xs rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-soft)] px-6 py-5 text-center text-sm">
        <p className="font-medium">No APIs match your filters</p>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Toggle a category back on, clear the search, or relax the support filter.
        </p>
        <button
          type="button"
          onClick={resetFilters}
          className="mt-3 inline-flex h-7 items-center rounded-md border border-[var(--color-accent)] bg-[var(--color-accent)]/10 px-2.5 text-xs font-medium text-[var(--color-accent)]"
        >
          Reset filters
        </button>
      </div>
      {hasSearch && <FilteredHint />}
    </div>
  )
}

export function Graph() {
  return (
    <ReactFlowProvider>
      <GraphInner />
    </ReactFlowProvider>
  )
}
